import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../auth/api.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { readLocalDrafts } from './dealDrafts.js';
import {
  DEFAULT_DEAL_ID,
  STORAGE_KEY,
  getDeal,
  getDefaultDeal,
  listDeals,
  parseDealFromHash,
  readStoredDealId,
  resolveDealId,
  setDraftDeals,
  writeStoredDealId,
} from './registry.js';

const DealContext = createContext(null);

/** Seed local drafts before first getDeal resolve (hash / storage). */
function seedLocalDraftsSync() {
  if (typeof window === 'undefined') return;
  setDraftDeals(readLocalDrafts());
}

function initialDealId() {
  if (typeof window === 'undefined') return DEFAULT_DEAL_ID;
  seedLocalDraftsSync();
  const { dealIdFromHash } = parseDealFromHash(window.location.hash);
  if (dealIdFromHash) return dealIdFromHash;
  return readStoredDealId() || DEFAULT_DEAL_ID;
}

function peekRawStoredDealId() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function DealProvider({ children }) {
  const { isAdmin, isAuthed } = useAuth();
  const [dealId, setDealIdState] = useState(initialDealId);
  const [draftTick, setDraftTick] = useState(0);
  const [selectorForced, setSelectorForced] = useState(() => {
    if (typeof window === 'undefined') return false;
    // initialDealId already seeded; re-seed is cheap and keeps order-independent.
    seedLocalDraftsSync();
    const { viewProjects, dealIdFromHash } = parseDealFromHash(window.location.hash);
    // Explicit #projects, or first visit with neither hash deal nor storage.
    if (viewProjects) return true;
    if (dealIdFromHash) return false;
    return !readStoredDealId();
  });

  const refreshDrafts = useCallback(async () => {
    let drafts = readLocalDrafts();
    if (isAdmin && isAuthed) {
      try {
        const res = await authApi.listDealDrafts();
        if (Array.isArray(res?.drafts)) {
          drafts = res.drafts;
        }
      } catch {
        /* keep localStorage drafts when API unavailable */
      }
    }
    setDraftDeals(drafts);
    setDraftTick((n) => n + 1);

    // After API/local overlay lands, recover a stored/hashed draft id that was
    // unknown on first paint. Never replace a valid non-default published selection
    // unless the preferred id is itself a known deal (incl. draft).
    if (typeof window === 'undefined') return;
    const { dealIdFromHash } = parseDealFromHash(window.location.hash);
    const preferred = dealIdFromHash || peekRawStoredDealId();
    if (!preferred || !getDeal(preferred)) return;
    setDealIdState((current) => {
      if (current === preferred) return current;
      // Upgrade only when we still look like a fallback to the default deal.
      if (current === DEFAULT_DEAL_ID && preferred !== DEFAULT_DEAL_ID) {
        writeStoredDealId(preferred);
        const { viewProjects } = parseDealFromHash(window.location.hash);
        // Recovered a real selection — don't keep the "no stored deal" selector force.
        if (!viewProjects) setSelectorForced(false);
        return preferred;
      }
      return current;
    });
  }, [isAdmin, isAuthed]);

  useEffect(() => {
    refreshDrafts();
  }, [refreshDrafts]);

  const selectDeal = useCallback((nextId, { openProjects = false } = {}) => {
    const id = resolveDealId(nextId);
    writeStoredDealId(id);
    setDealIdState(id);
    setSelectorForced(Boolean(openProjects));
  }, []);

  const openProjectSelector = useCallback(() => {
    setSelectorForced(true);
    if (typeof window !== 'undefined') {
      window.location.hash = 'projects';
    }
  }, []);

  const closeProjectSelector = useCallback(() => {
    setSelectorForced(false);
  }, []);

  const value = useMemo(() => {
    const deal = getDeal(dealId) || getDefaultDeal();
    return {
      dealId: deal.dealId,
      deal,
      deals: listDeals({ includeDrafts: true }),
      selectDeal,
      openProjectSelector,
      closeProjectSelector,
      showProjectSelector: selectorForced,
      setShowProjectSelector: setSelectorForced,
      refreshDrafts,
    };
  }, [
    dealId,
    selectDeal,
    openProjectSelector,
    closeProjectSelector,
    selectorForced,
    refreshDrafts,
    draftTick,
  ]);

  return <DealContext.Provider value={value}>{children}</DealContext.Provider>;
}

export function useDeal() {
  const ctx = useContext(DealContext);
  if (!ctx) {
    throw new Error('useDeal must be used within DealProvider');
  }
  return ctx;
}
